import {useCallback, useEffect, useRef, useState} from 'react';
import {
  useParsePDFMutation,
  useRunPDFFootingsMutation,
  useLazyGetJobStatusQuery,
} from '../../../data/pdfReviewApi';
import _ from 'lodash';
import {sleep} from '../../../util';

export const STATUS = {
  IDLE: 'idle',
  PARSING: 'parsing',
  PARSED: 'parsed',
  RUNNING_FOOTINGS: 'running_footings',
  COMPLETE: 'complete',
  ERROR: 'error',
};
const DEFAULT_WAIT_MS = 5000; // Retry every 5 seconds
const DEFAULT_RETRY_MAX = 180; // Retry for 15 minutes

const CACHE_PREFIX = 'pdf-footings-cache';
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const getCacheKey = (documentId) => `${CACHE_PREFIX}-${documentId}`;

const getCachedFootings = (documentId) => {
  try {
    const raw = localStorage.getItem(getCacheKey(documentId));
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (Date.now() - cached.timestamp > CACHE_TTL_MS) {
      localStorage.removeItem(getCacheKey(documentId));
      return null;
    }
    return cached.data;
  } catch {
    return null;
  }
};

const setCachedFootings = (documentId, data) => {
  try {
    localStorage.setItem(
      getCacheKey(documentId),
      JSON.stringify({data, timestamp: Date.now()})
    );
  } catch {}
};

const clearCachedFootings = (documentId) => {
  try {
    localStorage.removeItem(getCacheKey(documentId));
  } catch {}
};

const cleanupExpiredCache = () => {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(CACHE_PREFIX))
      .forEach((key) => {
        try {
          const cached = JSON.parse(localStorage.getItem(key));
          if (Date.now() - cached.timestamp > CACHE_TTL_MS) {
            localStorage.removeItem(key);
          }
        } catch {
          localStorage.removeItem(key);
        }
      });
  } catch {}
};

const parseFootingResults = (tables) => {
  if (!tables || tables.length === 0) {
    return {items: {}, openItems: {}, numItems: 0, numOpenItems: 0};
  }

  const items = {};
  const openItems = {};

  tables.forEach((table) => {
    if (!table.footings) return;

    table.footings.forEach((footing) => {
      if (footing.dismissed) return;

      const item = {
        ...footing,
        table_id: table.table_id,
        page_number: table.page_number,
        type: 'pdf-footings',
      };

      items[footing.id] = item;

      if (!footing.is_footing_correct) {
        openItems[footing.id] = item;
      }
    });
  });

  return {
    items,
    openItems,
    numItems: Object.keys(items).length,
    numOpenItems: Object.keys(openItems).length,
  };
};

export const usePdfFootingCheck = ({
  documentId,
  autoStart = false,
  isPdfParsed = false,
}) => {
  const [status, setStatus] = useState(() =>
    getCachedFootings(documentId) ? STATUS.COMPLETE : STATUS.IDLE
  );
  const [footingsData, setFootingsData] = useState(
    () => getCachedFootings(documentId) || null
  );
  const [error, setError] = useState(null);

  const isMountedRef = useRef(true);
  const hasStartedRef = useRef(false);

  // Ref to let startCheck know if we already have results without
  // adding footingsData to its dependency array.
  const footingsDataRef = useRef(footingsData);
  useEffect(() => {
    footingsDataRef.current = footingsData;
  }, [footingsData]);

  const [parsePDF] = useParsePDFMutation();
  const [runPDFFootings] = useRunPDFFootingsMutation();
  const [getJobStatus] = useLazyGetJobStatusQuery();

  const startParsing = useCallback(async () => {
    if (!documentId) return;

    setStatus(STATUS.PARSING);
    setError(null);

    try {
      await parsePDF({documentId}).unwrap();

      if (!isMountedRef.current) return;

      setStatus(STATUS.PARSED);
    } catch (err) {
      if (!isMountedRef.current) return;
      console.error('[usePdfFootingCheck] Parse error:', err);
      setError(err?.data?.message || 'Failed to parse PDF.');
      setStatus(STATUS.ERROR);
      throw err; // Re-throw to stop the chain
    }
  }, [documentId, parsePDF]);

  const startFootingsCheck = useCallback(async () => {
    if (!documentId) return;

    setStatus(STATUS.RUNNING_FOOTINGS);
    setError(null);

    try {
      const runPDFFootingsResult = await runPDFFootings({documentId}).unwrap();

      if (!isMountedRef.current) return;

      const jobId = runPDFFootingsResult?.job_id;
      if (jobId == null) {
        setError('No job ID from footings check.');
        setStatus(STATUS.ERROR);
        return;
      }

      // Always poll for results; the start API only returns a job ID
      await checkJobStatus(jobId);
    } catch (err) {
      if (!isMountedRef.current) return;
      console.error('[usePdfFootingCheck] Footings error:', err);
      setError(err?.data?.message || 'Failed to run footings check.');
      setStatus(STATUS.ERROR);
    }
  }, [documentId, runPDFFootings]);

  const checkJobStatus = async (jobId) => {
    if (_.isNil(jobId)) {
      console.error('No job ID found', {jobId});
      return;
    }

    const pollJobStatus = async (tries = 0) => {
      if (!window.location.href.includes(documentId)) {
        console.log('Job has been cancelled', {jobId, documentId, tries});
        return;
      }
      try {
        const _jobResult = await getJobStatus(
          {jobId, checkType: 'pdf-footings'},
          false
        ).unwrap();

        if (_jobResult.status === 'completed') {
          const _data = _jobResult.results?.footings;
          const parsed = parseFootingResults(_data);
          setFootingsData(parsed);
          setCachedFootings(documentId, parsed);
          setStatus(STATUS.COMPLETE);
        } else if (_.includes(['started', 'processing'], _jobResult.status)) {
          console.log('Job is processing', {
            jobId,
            checkType: 'pdf-footings',
            tries,
          });
          if (tries < DEFAULT_RETRY_MAX) {
            await sleep(DEFAULT_WAIT_MS);
            return pollJobStatus(tries + 1);
          } else {
            console.error('Too many retries, aborting.', {
              jobId,
              checkType: 'pdf-footings',
            });
            setStatus(STATUS.ERROR);
          }
        } else if (_jobResult.status === 'failed') {
          console.error('Copilot job failed', {
            jobId,
            checkType: 'pdf-footings',
          });
          setError(_jobResult.user_message || null);
          setStatus(STATUS.ERROR);
        }
      } catch (_error) {
        console.error('Error processing job', {error: _error});
        setStatus(STATUS.ERROR);
      }
    };

    // Start polling with initial count of 0
    await pollJobStatus(0);
  };

  const statusRef = useRef(status);
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const startCheck = useCallback(async () => {
    if (
      footingsDataRef.current ||
      statusRef.current === STATUS.RUNNING_FOOTINGS
    ) {
      return;
    }
    try {
      if (!isPdfParsed) {
        await startParsing();
      }
      if (isMountedRef.current) {
        await startFootingsCheck();
      }
    } catch {
      // Error already handled in startParsing
    }
  }, [startParsing, startFootingsCheck, isPdfParsed]);

  const rerunFootingsCheck = useCallback(() => {
    clearCachedFootings(documentId);
    footingsDataRef.current = null;
    setFootingsData(null);
    startFootingsCheck();
  }, [documentId, startFootingsCheck]);

  const dismissFooting = useCallback(
    (footingId) => {
      setFootingsData((prev) => {
        if (!prev) return prev;
        const newItems = {...prev.items};
        const newOpenItems = {...prev.openItems};
        delete newItems[footingId];
        delete newOpenItems[footingId];
        const updated = {
          items: newItems,
          openItems: newOpenItems,
          numItems: Object.keys(newItems).length,
          numOpenItems: Object.keys(newOpenItems).length,
        };
        setCachedFootings(documentId, updated);
        return updated;
      });
    },
    [documentId]
  );

  const reset = useCallback(() => {
    clearCachedFootings(documentId);
    setStatus(STATUS.IDLE);
    setFootingsData(null);
    setError(null);
    hasStartedRef.current = false;
    footingsDataRef.current = null;
  }, [documentId]);

  // cleanup
  useEffect(() => {
    cleanupExpiredCache();
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // auto-start check when documentId is available
  useEffect(() => {
    if (autoStart && documentId && !hasStartedRef.current) {
      hasStartedRef.current = true;
      startCheck();
    }
  }, [autoStart, documentId, startCheck]);

  return {
    status,
    footingsData,
    error,
    isLoading: status === STATUS.PARSING || status === STATUS.RUNNING_FOOTINGS,
    isParsing: status === STATUS.PARSING,
    isRunningFootings: status === STATUS.RUNNING_FOOTINGS,
    isComplete: status === STATUS.COMPLETE,
    isError: status === STATUS.ERROR,
    hasErrors: (footingsData?.numOpenItems ?? 0) > 0,
    startCheck,
    rerunFootingsCheck,
    dismissFooting,
    reset,
  };
};

export default usePdfFootingCheck;
